// Guided product tours.
//
// Users reported not knowing which features exist, so each service page gets a
// short, numbered walkthrough that spotlights the real UI (no screenshots, no
// fake copies of the controls). Steps are declarative: a CSS selector points at
// the live element and the overlay measures it every frame-ish, so a step still
// works when the element is inside a modal, a scrolled ag-Grid row, or appears
// only after an upload finishes.
//
// Selector contract: every element a step points at carries a `data-tour="..."`
// attribute at its source, so restyling a page can't silently break a tour.

export type TourPlacement = 'auto' | 'top' | 'bottom' | 'left' | 'right';

export type TourStep = {
  /** Stable id — handy for analytics / debugging. */
  id: string;
  title: string;
  body: string;
  /**
   * Live element to spotlight. Omit for a purely informational card. A list is a
   * priority order — the first selector that is actually on screen wins, which
   * lets a step follow the user: spotlight the panel once it's open, otherwise
   * the control that opens it.
   */
  target?: string | string[];
  placement?: TourPlacement;
  /** Extra breathing room around the spotlight hole, in px. */
  spotlightPadding?: number;
  /** Corner radius of the spotlight ring — match the element it frames. */
  spotlightRadius?: number;
  /**
   * Widen the hole from the matched target out to its nearest ancestor matching
   * this selector — for steps about a whole record rather than one control in it.
   *
   * Why this rather than simply targeting the ancestor: target selection also
   * decides whether a step is on screen and whether something is covering it, and
   * those checks are tuned for small, specific elements. Aiming a target at a
   * table row instead made it lose to the cell selectors and the spotlight stayed
   * on the cell. Keeping the reliable small target and expanding only the measured
   * box gets the row lit without touching any of that logic — and it cannot pick
   * the wrong row, since it starts from a cell already known to be in the right one.
   */
  spotlightAncestor?: string;
  /**
   * Let clicks and drags through the dimmed area instead of the spotlight hole
   * being the only live region.
   *
   * For steps whose action cannot be confined to one rectangle: drawing a box
   * anywhere on a document page, then using a control that only appears once the
   * box exists. The dim and the ring still say where to work — they just stop
   * being a fence. Use sparingly: on an ordinary step the fence is what keeps the
   * user on the one control the tour is talking about.
   */
  allowPageInteraction?: boolean;
  /**
   * Demonstrate the gesture the step is asking for, inside the spotlight.
   *
   * `drag-region` loops a dashed box drawing itself under a moving pointer. Prose
   * alone leaves "drag a box" ambiguous — people reach for a click, or a text
   * selection — and this is the one step whose action is a gesture rather than a
   * press, so there is nothing to point an arrow at.
   *
   * `click` pulses an outline around the control and taps it with a pointer. For
   * steps whose spotlight is widened to a whole record (`spotlightAncestor`): the
   * ring then frames a table row with four controls in it and says nothing about
   * which one to press. It anchors to the MATCHED target, not the widened hole,
   * so it always lands on the button the step is about.
   *
   * Both disappear as soon as the user presses anywhere, since by then they have
   * understood, and neither ever takes the click itself.
   */
  gestureHint?: 'drag-region' | 'click';
  /**
   * Offer a sample PDF to download from the step's card.
   *
   * The walkthrough asks the user to upload a document of their own, which is a
   * dead end for anyone who opened it without one — and that is common, since the
   * tour is offered to empty workspaces. Kept in the card rather than in the
   * upload panel so it reads as part of the walkthrough and does not become
   * permanent furniture on a screen everyone else uses daily.
   */
  offerSampleDocument?: boolean;
  /**
   * Where the card goes when the spotlight leaves no room beside it (a target
   * that nearly fills the viewport) or when there is no spotlight at all.
   * Defaults to bottom-right; this app puts primary modal actions there, so
   * steps that spotlight a modal should pick a different corner.
   */
  cardCorner?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  /**
   * Shown instead of the spotlight when `target` isn't on screen yet (e.g. the
   * Review button only exists once a file finished processing). The step still
   * explains itself so the walkthrough never dead-ends.
   */
  missingHint?: string;
  /**
   * Advance once this selector is on screen (the user did the action). A step
   * that declares any advance signal is an ACTION step: it shows "waiting for
   * you" instead of a Next button, so the tour can't be stepped past the thing it
   * is waiting for. A step with no signal is explanatory and gets a Next.
   */
  advanceWhenVisible?: string;
  /** Advance once this selector leaves the screen (e.g. a panel was closed). */
  advanceWhenGone?: string;
  /** Advance once the URL contains this fragment (the user navigated). */
  advanceWhenRoute?: string;
  /**
   * Makes the step CONDITIONAL: it only runs when one of these selectors is on
   * screen as the step opens, otherwise the tour moves straight past it (and
   * stops counting it, so the "Step 6 of 15" footer never jumps). Use for
   * states the walkthrough must explain when they happen but that most runs
   * never hit — services powering on, say. Evaluated once, on entry: a step
   * that has started is never retroactively dropped.
   */
  onlyWhenVisible?: string | string[];
  /**
   * Selector to click on the user's behalf when the step opens, retrying until it
   * exists. Use where the step IS "this panel is now open" rather than "press
   * this". Skipped if the step's primary target is already on screen. A list is
   * tried in order, so a row-scoped selector can fall back to an unscoped one.
   */
  autoOpen?: string | string[];
};

export type Tour = {
  id: string;
  name: string;
  /** Pathname fragment the tour starts on; the provider routes there first. */
  routeMatch?: string;
  /** Where to navigate when the tour is started from another page. */
  routeTo?: string;
  /**
   * Every pathname fragment the tour may span. A tour that follows the user from
   * the document list into the review page lives on more than one route, so the
   * provider can only end it when the path matches NONE of these.
   */
  routeScope?: string[];
  intro: {
    title: string;
    body: string;
    /** Duration hint shown in the prompt footer. */
    duration?: string;
    bullets: { label: string; caption?: string }[];
  };
  steps: TourStep[];
};

export const DOCUMENT_TOUR_ID = 'document-processing';

/**
 * The document the tour is walking through is the one just uploaded, which the
 * grid puts in its first row (ag-Grid stamps `row-index` on every row element).
 * Row-level selectors are scoped to it so an unrelated document further down the
 * table — one that finished processing long ago and already shows Review Now —
 * can't drive the tour or steal its spotlight.
 */
const inTourRow = (selector: string) => `[row-index="0"] ${selector}`;

/**
 * Passed as `spotlightAncestor` by steps about the whole record: the spotlight is
 * measured out to the grid row containing whichever cell the step matched, so the
 * document name, tactic details, Progress and Status stay readable rather than
 * dimmed behind the mask. The mask only blocks input OUTSIDE the hole, so the
 * row's own controls remain clickable.
 *
 * Both forms are listed because `row-index` is the attribute the tour already
 * relies on (see `inTourRow`) while `.ag-row` is only ag-Grid's current class
 * name — if that class changes, `closest()` still finds the row.
 */
const TOUR_ROW = '.ag-row[row-index="0"], [row-index="0"]';

/** "Whichever of these exists" — a CSS selector list, for a single-selector field. */
const anyOf = (...selectors: string[]) => selectors.join(', ');

/**
 * The review panel's three tabs, and the two panel bodies that only exist while
 * their tab is open. That difference is what the tour relies on: `autoOpen` skips
 * clicking when the step's first target is already on screen, so a step whose
 * first target is the PANEL will open the tab, while one pointed at the tab
 * itself never would.
 *
 * The ISI and References tabs are hidden altogether for tactics marked
 * "No ISI Needed", which is what lets those steps skip themselves.
 */
/**
 * The review page has finished loading — both the annotations JSON (header counts
 * and findings sidebar) and the raw text the PDF pane needs. Views publishes it as
 * `data-review-ready` on its always-present shell.
 *
 * The tour enters the review steps on THIS rather than on the route change: the
 * page shell paints immediately with per-section skeletons, so arriving on
 * navigation alone spotlighted a skeleton and then jumped as the real content
 * pushed the layout around.
 */
const REVIEW_READY = '[data-tour="review-shell"][data-review-ready="true"]';

const CLAIMS_TAB = '[data-tour="review-claims-tab"]';
const ISI_TAB = '[data-tour="review-isi-tab"]';
const ISI_PANEL = '[data-tour="review-isi-panel"]';
const REFERENCES_TAB = '[data-tour="review-references-tab"]';
const REFERENCES_PANEL = '[data-tour="review-references-panel"]';

/**
 * The engine's power state, published by the status pill in the documents
 * header (`data-service-state`). Processing is queued rather than refused while
 * it is off or warming up, so the tour has a step for that wait.
 */
const SERVICE_PILL = '[data-tour="service-status"]';
const SERVICE_WARMING = anyOf(
  `${SERVICE_PILL}[data-service-state="inactive"]`,
  `${SERVICE_PILL}[data-service-state="creating"]`
);

const documentProcessingTour: Tour = {
  id: DOCUMENT_TOUR_ID,
  name: 'Document processing',
  routeMatch: '/folder',
  routeTo: '/dashboard/folder/1',
  // The tour follows the document into the review page, so it spans both routes.
  routeScope: ['/folder', '/views'],
  intro: {
    title: 'Take a quick tour of document review',
    body: 'A hands-on walkthrough that takes one of your own documents from upload all the way through MLR review.',
    duration: 'Takes about 5 minutes',
    bullets: [
      { label: 'Upload a document', caption: 'Bring a PDF into the workspace' },
      {
        label: 'Set its tactic details',
        caption: 'Product, audience, channel and ISI expectations'
      },
      {
        label: 'Process it',
        caption: 'The engine scans for claim, ISI and reference issues'
      },
      {
        label: 'Review every finding',
        caption: 'Approve, flag and comment your way to sign-off'
      }
    ]
  },
  steps: [
    {
      id: 'upload-button',
      title: 'Start with an upload',
      body: 'Everything begins here. Click Upload Documents to bring regulated content into the workspace — PDFs are supported.',
      target: '[data-tour="docs-upload"]',
      placement: 'bottom',
      advanceWhenVisible: '[data-tour="adoc-dropzone"]'
    },
    {
      id: 'choose-file',
      title: 'Choose your document',
      body: 'Click this area (or drag a file onto it) and pick the PDF you want reviewed. You can add more than one.',
      target: '[data-tour="adoc-dropzone"]',
      placement: 'left',
      // This is the step the walkthrough stalls on for anyone who came without a
      // PDF, so the way out belongs right here on its card.
      offerSampleDocument: true,
      missingHint:
        'Click Upload Documents to open this panel — the tour will follow you in.',
      advanceWhenVisible: '[data-tour="adoc-file"]'
    },
    {
      id: 'finish-upload',
      title: 'Finish uploading',
      body: 'Press Finish Uploading to send the file in. Your document appears at the top of the table when it lands — the tour carries on from there.',
      target: '[data-tour="adoc-submit"]',
      placement: 'left',
      spotlightPadding: 8,
      missingHint: 'Reopen the upload panel to continue where you left off.',
      advanceWhenGone: '[data-tour="adoc-dropzone"]'
    },
    {
      id: 'tactic-details',
      title: 'Update tactic details',
      body: "We've opened Tactic Details for your new document. Set the product, audience, channel and ISI expectations here — the engine uses them to select the right rules and library files, so filling them in first gives you a far more accurate review.",
      // Once the panel is up it becomes the spotlight, so it reads as highlighted
      // rather than dimmed behind the tour; before that, the row control that
      // opens it does.
      target: [
        '[data-tour="tactic-modal"]',
        inTourRow('[data-tour="tactic-open"]'),
        inTourRow('[data-tour="tactic-details"]')
      ],
      placement: 'auto',
      spotlightPadding: 6,
      spotlightRadius: 24, // matches the modal paper's radius
      // Keep clear of the panel's bottom-right Save Changes button.
      cardCorner: 'bottom-left',
      // NO autoOpen. This step used to click `[row-index="0"] tactic-open`
      // itself, which opened — and therefore saved — the WRONG document: the
      // upload panel closes about two seconds before the refetch that brings the
      // new row in, so at the moment this step starts the top row is still the
      // previously uploaded document. The grid now opens the panel by docId
      // instead, off the row the refetch actually added
      // (AllFiles/index.tsx `openTacticsForNewRow`). If that never happens the
      // step falls back to spotlighting the row's own Tactic Details icon, which
      // is inside the hole and stays clickable.
      missingHint:
        'Waiting for your document to appear in the table, then Tactic Details opens automatically.',
      // Saving (or closing) the panel unmounts it — that's the cue to move on.
      advanceWhenGone: '[data-tour="tactic-modal"]'
    },
    {
      id: 'process-document',
      title: 'Process the document',
      body: 'Click Process Now to hand the document to the engine. It asks you to confirm the tactic details first — nothing is processed until you do.',
      // Matched on the button — which is what reliably resolves — then widened to
      // the whole row, so the document being processed stays readable while the
      // user decides instead of them clicking Process Now on a record they can no
      // longer see. Process Now is inside the hole, so it stays the only thing
      // here they can click.
      target: inTourRow('[data-tour="process-btn"]'),
      spotlightAncestor: TOUR_ROW,
      // The hole is the whole row, which lights Process Now, the bin and the
      // info icon equally — so mark the one the step actually means.
      gestureHint: 'click',
      placement: 'auto',
      spotlightPadding: 4,
      spotlightRadius: 10,
      missingHint:
        'Process Now appears in the Actions column for any newly uploaded document.',
      // The confirmation panel opening is the signal here — NOT Process Now
      // disappearing. The button stays mounted behind that panel and only goes
      // once processing actually begins, which would be a step too late to
      // introduce the confirmation.
      advanceWhenVisible: '[data-tour="confirm-modal"]'
    },
    {
      id: 'confirm-tactics',
      title: 'Confirm before you validate',
      body: 'A last look at what the engine will validate against. Check the summary, tick the three confirmations — or jump back with Edit tactic details if something is wrong — then Confirm & process document. Getting this right here means far fewer bad findings later.',
      target: '[data-tour="confirm-modal"]',
      placement: 'auto',
      spotlightPadding: 6,
      spotlightRadius: 22, // matches .cfm-modal-paper
      // Least functional corner of this panel: the decorative brand rail.
      cardCorner: 'top-left',
      advanceWhenGone: '[data-tour="confirm-modal"]'
    },
    {
      // Only when the engine is off / still coming up at the moment the user
      // confirms. The app already queues the document and processes it by
      // itself once services report InService, so this step exists to say so —
      // without it the tour would sit on "Processing…" pointing at a row that
      // still reads Uploaded, and the user would think their click was lost.
      id: 'service-warmup',
      title: 'Powering up the engine',
      body: 'The processing engine is asleep, so we are starting it for you — that usually takes a few minutes. Your document is already queued: the moment this pill turns green it goes off to be processed on its own, with nothing more for you to click. The tour picks up again as soon as it does.',
      target: SERVICE_PILL,
      placement: 'bottom',
      spotlightPadding: 8,
      spotlightRadius: 999, // the pill's own radius
      onlyWhenVisible: SERVICE_WARMING,
      missingHint:
        'The engine status pill sits in the header, next to Upload Documents.',
      // Whichever comes first: the row flipping to Processing, or — for a very
      // quick document — straight to Review Now. Waiting on the pill turning
      // green instead would move the tour on while the row still read Uploaded.
      advanceWhenVisible: anyOf(
        inTourRow('[data-tour="processing-status"]'),
        inTourRow('[data-tour="review-btn"]')
      )
    },
    {
      id: 'processing-wait',
      title: 'Processing…',
      body: 'The engine is reading your document now — extracting text, matching claims against approved sources and checking ISI and references. Progress and Status update live, and you can keep working elsewhere; longer documents take a few minutes.',
      // Everything that moves while the engine works — the name, Progress,
      // Status — is spread across the row, so lighting one cell left the rest
      // dimmed behind the mask and the user could not watch the very thing this
      // step tells them to watch. Match a cell, then widen to the row.
      target: [
        inTourRow('[data-tour="processing-status"]'),
        inTourRow('[data-tour="doc-progress"]')
      ],
      spotlightAncestor: TOUR_ROW,
      placement: 'auto',
      // Tight to the row: it is already full-table-width, so generous padding
      // only eats into the header and the row below.
      spotlightPadding: 4,
      spotlightRadius: 10,
      missingHint:
        'Watch the Progress and Status columns — the tour continues as soon as this document is ready to review.',
      // Review Now only appears once the document reaches Completed, which makes
      // it the cleanest "processing has finished" signal there is.
      advanceWhenVisible: inTourRow('[data-tour="review-btn"]')
    },
    {
      id: 'open-review',
      title: 'Open the review',
      body: 'Your document is ready. Click Review Now to open it beside its findings — the tour carries on inside the review workspace.',
      target: inTourRow('[data-tour="review-btn"]'),
      placement: 'auto',
      spotlightPadding: 8,
      missingHint:
        'Opening your review — the walkthrough continues once the document and its findings have loaded.',
      // Not `advanceWhenRoute: '/views'`: the route changes the instant the click
      // lands, which put the next step on screen while the review page was still
      // all skeletons. Waiting for the page to report itself ready means the first
      // review step spotlights real content that will not move under it.
      advanceWhenVisible: REVIEW_READY
    },

    // ---- Document review page ----------------------------------------------
    {
      id: 'review-header',
      title: 'Your review scoreboard',
      body: 'The header carries the document name, its tactic type, and running counts: how many findings you have Approved, how many you have Flagged, how many the engine detected on its own and how many you added yourself. The badge on the right is the total still to work through.',
      target: '[data-tour="review-header"]',
      placement: 'bottom',
      spotlightPadding: 6,
      spotlightRadius: 14
    },
    {
      id: 'review-pdf',
      title: 'Your document, annotated',
      body: 'The document renders here with every finding highlighted in place. Click a highlight to jump the review panel to it, and select any passage of your own to raise a finding the engine missed.',
      target: '[data-tour="review-pdf"]',
      placement: 'right',
      spotlightPadding: 4,
      spotlightRadius: 14,
      cardCorner: 'top-right'
    },
    {
      id: 'review-tabs',
      title: 'Claims, ISI and References',
      body: 'The review panel is split by what is being checked: Claims covers claim and brand issues, ISI checks the Important Safety Information, and References checks citations and approved sources. Each tab keeps its own list of findings.',
      target: '[data-tour="review-tabs"]',
      placement: 'auto',
      spotlightPadding: 6,
      spotlightRadius: 12
    },
    {
      id: 'review-export',
      title: 'Sort, and export when you are done',
      // Sits with the tabs because both are panel chrome: this says what the
      // controls around the findings list do, before the tour drops into the
      // findings themselves. Deliberately not a closing summary any more — it is
      // no longer the last step.
      body: 'Sort By reorders the findings — by severity, by section, by type — so you can triage the worst first. And when the review is finished, the export button hands you the full findings list to share or file.',
      target: '[data-tour="review-sort"]',
      // Sort By lives in the claims sidebar. Nothing has switched tabs by this
      // point, so this is only a safety net for a user who wandered off Claims
      // on their own — autoOpen no-ops when the target is already on screen.
      autoOpen: CLAIMS_TAB,
      placement: 'auto',
      spotlightPadding: 8
    },
    {
      id: 'review-pager',
      title: 'One finding at a time',
      body: 'The panel walks you through findings one by one — the counter shows where you are, and the arrows move between them. Every finding you move to is highlighted in the document alongside.',
      target: '[data-tour="review-pager"]',
      placement: 'auto',
      spotlightPadding: 8
    },
    {
      id: 'review-finding',
      title: 'What a finding tells you',
      body: 'For each one you get the exact sentence, why it was flagged, the approved source it was matched against, and what the engine expected to see instead — everything you need to make a call without leaving the page.',
      target: '[data-tour="review-finding"]',
      placement: 'auto',
      spotlightPadding: 6,
      spotlightRadius: 14,
      cardCorner: 'top-left',
      missingHint:
        'Finding details fill this panel once the document has findings to show.'
    },
    {
      id: 'review-actions',
      title: 'Approve, flag or comment',
      body: 'This is the decision itself. Approve clears a finding, Flag marks it as a real issue to fix, and Add comment leaves a note for the rest of the MLR team. Every action is recorded, so the review trail stays audit-ready.',
      target: '[data-tour="review-actions"]',
      placement: 'auto',
      spotlightPadding: 8,
      missingHint:
        'Approve / Flag / Add comment sit under the highlighted sentence of each finding.'
    },
    {
      // Still the Claims tab: this is the one thing on this page the user does TO
      // the document rather than reads from it, so it belongs with claims review
      // and before the tour moves on to the other two tabs.
      id: 'pdf-user-highlight',
      title: 'Add your own supporting sentence',
      body: 'Not every flagged claim is unsupported — sometimes the evidence is right there in the document. Try it now: drag a box around a sentence on the page, and draw more boxes to add them to the same one. A bar appears with the count — click Annotate Text and it pulls the wording out for you, then pick the claim it supports and Save. It is stored as your own highlight, in your name, alongside what the engine found.',
      // The whole document pane, because the drag can start anywhere in it — a
      // spotlight on the "Drag a box" prompt alone framed a hint the user cannot
      // act on. The prompt sits inside this pane, so it is still lit.
      target: '[data-tour="review-pdf"]',
      // Drawing a region is a drag across arbitrary parts of the page, and the
      // Annotate Text bar only exists once a box is drawn — neither fits inside a
      // single spotlight rectangle, so this step lets input through everywhere.
      allowPageInteraction: true,
      // Show the gesture rather than only describing it: "drag a box" is read as a
      // click or a text selection often enough to be worth demonstrating.
      gestureHint: 'drag-region',
      // The prompt only renders where selection is allowed, i.e. the Claims tab —
      // so if the user (or an earlier step) left another tab open, click back.
      autoOpen: CLAIMS_TAB,
      placement: 'right',
      spotlightPadding: 4,
      spotlightRadius: 14,
      cardCorner: 'top-right',
      missingHint:
        'Open the Claims tab — drawing your own supporting sentence is available on the document there.'
    },
    {
      id: 'review-isi',
      title: 'Important Safety Information',
      body: 'The ISI tab checks the safety copy in its own right: whether the required statements are present, whether they read as approved, and whether anything is missing or altered. Findings work exactly as they do for claims — the sentence is highlighted in the document alongside, and you approve, flag or comment on each one.',
      // The panel first so the spotlight lands on the findings once the tab opens;
      // the tab itself is the fallback for the moment before it renders.
      target: [ISI_PANEL, ISI_TAB],
      autoOpen: ISI_TAB,
      // Documents whose tactic needs no ISI hide the tab outright, and this step
      // has nothing to say about them.
      onlyWhenVisible: ISI_TAB,
      placement: 'auto',
      spotlightPadding: 6,
      spotlightRadius: 12,
      missingHint:
        'Important Safety Information is the middle tab above the findings list.'
    },
    {
      id: 'review-references',
      title: 'References and citations',
      body: 'The References tab follows the other half of the trail: every citation in the piece, matched back to the approved source it draws on. Selecting a reference highlights the passage it supports in the document, so you can confirm the citation actually carries the claim it is attached to — and flag it here if it does not.',
      target: [REFERENCES_PANEL, REFERENCES_TAB],
      autoOpen: REFERENCES_TAB,
      onlyWhenVisible: REFERENCES_TAB,
      placement: 'auto',
      spotlightPadding: 6,
      spotlightRadius: 12,
      missingHint: 'References is the last tab above the findings list.'
    },
  ]
};

export const TOURS: Record<string, Tour> = {
  [DOCUMENT_TOUR_ID]: documentProcessingTour
};

/** Tour offered right after login (and by the header button). */
export const DEFAULT_TOUR_ID = DOCUMENT_TOUR_ID;
